
import { internalAuthService } from '../internalAuthService';
import { supabaseUserSearchService } from './supabase/userSearchService';
import { User } from '@/types/chat';
import { AgeGroup } from '@/types/user';
import { isAgeCompatible } from '@/utils/ageUtils';

export const userService = {
  // Search users by username
  async searchUsers(query: string): Promise<User[]> {
    console.log('=== USER SERVICE SEARCH ===');
    console.log('Searching users with query:', query);
    
    if (!query || query.trim().length < 1) {
      return [];
    }

    try {
      // Clean the query - remove @ symbol if present
      const cleanQuery = query.startsWith('@') ? query.slice(1) : query;
      console.log('Clean search query:', cleanQuery);

      // Search both internal and Supabase users in parallel
      const [internalUsers, supabaseUsers] = await Promise.all([
        internalAuthService.searchUsers(cleanQuery),
        supabaseUserSearchService.searchUsers(cleanQuery)
      ]);

      // Convert internal users to User format
      const internalUserResults = internalUsers.map(user => ({
        id: user.id,
        username: user.username,
        first_name: user.username, // Use username as fallback for first_name
        last_name: '',
        role: user.role,
        user_type: 'internal' as const
      }));

      // Supabase users are already in the correct format
      const allUsers = [...internalUserResults, ...supabaseUsers];

      console.log('Internal users found:', internalUserResults.length);
      console.log('Supabase users found:', supabaseUsers.length);
      console.log('Total search results:', allUsers.length);

      // Log specific users for debugging
      allUsers.forEach(user => {
        console.log(`Found user: ${user.username} (${user.user_type}) - ID: ${user.id}`);
      });

      return allUsers;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  },

  // Get all users (for adding to groups)
  async getAllUsers(): Promise<User[]> {
    console.log('=== USER SERVICE GET ALL ===');
    console.log('Getting all users');
    
    try {
      // Get both internal and Supabase users
      const [internalUsers, supabaseUsers] = await Promise.all([
        internalAuthService.getAllUsers(),
        supabaseUserSearchService.getAllUsers()
      ]);

      // Convert internal users to User format
      const internalUserResults = internalUsers.map(user => ({
        id: user.id,
        username: user.username,
        first_name: user.username, // Use username as fallback for first_name
        last_name: '',
        role: user.role,
        user_type: 'internal' as const,
        age_group: user.age_group
      }));

      // Supabase users are already in the correct format
      const allUsers = [...internalUserResults, ...supabaseUsers];

      console.log('Internal users fetched:', internalUserResults.length);
      console.log('Supabase users fetched:', supabaseUsers.length);
      console.log('Total users fetched:', allUsers.length);

      return allUsers;
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  },

  // Get age-compatible users for a specific game/team
  async getAgeCompatibleUsers(requiredAgeGroup: AgeGroup, roleFilter?: string): Promise<User[]> {
    console.log('=== USER SERVICE GET AGE COMPATIBLE ===');
    console.log('Required age group:', requiredAgeGroup);
    console.log('Role filter:', roleFilter);
    
    try {
      const allUsers = await this.getAllUsers();
      console.log('All users before filtering:', allUsers.map(u => ({ username: u.username, age_group: u.age_group, role: u.role })));
      
      // Filter by age compatibility and role if specified
      const compatibleUsers = allUsers.filter(user => {
        console.log(`\n--- Checking user: ${user.username} ---`);
        console.log(`User role: ${user.role}, age_group: ${user.age_group}`);
        
        // Only show players for safety (other roles can be manually added if needed)
        if (user.role !== 'player') {
          console.log(`❌ Filtered out ${user.username}: not a player (role: ${user.role})`);
          return false;
        }
        
        // Apply role filter if specified
        if (roleFilter && user.role !== roleFilter) {
          console.log(`❌ Filtered out ${user.username}: role filter mismatch`);
          return false;
        }
        
        // Check age compatibility - critical safety check
        if (user.age_group && requiredAgeGroup) {
          const isCompatible = isAgeCompatible(user.age_group as AgeGroup, requiredAgeGroup);
          console.log(`Age compatibility check: ${user.username} (${user.age_group}) vs required (${requiredAgeGroup}) = ${isCompatible}`);
          
          if (isCompatible) {
            console.log(`✅ ${user.username} is age compatible`);
          } else {
            console.log(`❌ ${user.username} is NOT age compatible`);
          }
          
          return isCompatible;
        }
        
        // If no age group data, default to incompatible for safety
        console.log(`❌ No age group data for user: ${user.username} - defaulting to incompatible for safety`);
        return false;
      });

      console.log(`\n=== FINAL RESULTS ===`);
      console.log(`Filtered ${allUsers.length} users to ${compatibleUsers.length} age-compatible users`);
      console.log('Compatible users:', compatibleUsers.map(u => ({ username: u.username, age_group: u.age_group })));
      
      return compatibleUsers;
    } catch (error) {
      console.error('Error fetching age-compatible users:', error);
      return [];
    }
  }
};
